// src/pages/dashboard/receptionist-settings/BusinessAndKnowledgeSettings.tsx
import { useState, useEffect, useRef, DragEvent, ChangeEvent } from 'react'; // Removed useMemo
import { useForm, Controller, Control, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'; // Added useQuery
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
// import { Switch } from '@/components/ui/switch';
// import { Progress } from "@/components/ui/progress"; // Removed Progress import
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
    Plus, Pencil, Trash2, Loader2, Copy, Link as LinkIcon, Save, Check, Brain, Database, 
    FileText, UploadCloud, AlertCircle, Briefcase, MessageSquareText, BookOpen, ExternalLink,
    Sparkles, CheckCircle2, Lightbulb, ChevronRight, PackageCheck
} from 'lucide-react'; 
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import InfoModal from '@/components/ui/InfoModal';
import { motion, AnimatePresence } from 'framer-motion';
import {
    SettingsFormData,
    QAPair,
    BusinessService,
    KnowledgeFile,
    QAItemFormData,
    SiteLinkFormData,
    ServiceItemFormData,
    QAPairType,
    UserSettings
} from './settingsTypes';
import {
    qaItemFormSchema,
    siteLinkFormSchema,
    serviceItemFormSchema
} from './settingsSchemas';
import { useVideoBySlug } from '@/hooks/useVideos';
import VideoLink from '@/components/ui/VideoLink';

interface UserHeroCommand {
  id: string;
  is_active: boolean;
  title: string;
  description: string;
}

interface UserHeroCommandRaw {
  id: string;
  is_active: boolean;
  hero_commands: {title: string; description: string}; 
}


interface BusinessAndKnowledgeSettingsProps {
    mainFormControl: Control<SettingsFormData>;
    mainFormErrors: FieldErrors<SettingsFormData>;
    mainFormRegister: UseFormRegister<SettingsFormData>;
    mainFormSetValue: UseFormSetValue<SettingsFormData>;
    settings: UserSettings | null | undefined;
    isSettingsFetching: boolean;
    copyToClipboard: (text: string | null | undefined) => void;
    isSetupComplete: boolean;
    allQaPairs: QAPair[] | undefined; 
    qaLoading: boolean;
    qaError: Error | null;
    businessServices: BusinessService[] | undefined;
    servicesLoading: boolean;
    servicesError: Error | null;
    knowledgeFiles?: KnowledgeFile[];
    filesLoading?: boolean;
    filesError?: Error | null;
    isMainFormDirty: boolean;
    isSavingOrSyncing: boolean;
    activeTab: string; 
    triggerVapiConfigUpdate?: () => Promise<void>;
    // getSettingsValues: () => SettingsFormData; // Removed: No longer needed for profile progress
}

// Animation variants for list items
const listItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring" as const,
            stiffness: 100
        }
    },
    exit: {
        opacity: 0,
        x: -20,
        transition: {
            duration: 0.2
        }
    }
};

const AnimatedIcon = ({ children, className }: { children: React.ReactNode, className?: string }) => (
    <motion.div
        whileHover={{ scale: 1.15, rotate: [0, 10, -10, 0] }}
        transition={{ duration: 0.3 }}
        className={className}
    >
        {children}
    </motion.div>
);

export default function BusinessAndKnowledgeSettings({
    mainFormControl,
    mainFormErrors,
    mainFormRegister,
    mainFormSetValue,
    settings,
    isSettingsFetching,
    copyToClipboard,
    isSetupComplete,    
    allQaPairs, 
    qaLoading,
    qaError,
    businessServices,
    servicesLoading,
    servicesError,
    knowledgeFiles,
    filesLoading,
    filesError,
    isMainFormDirty,
    isSavingOrSyncing,
    activeTab,
    triggerVapiConfigUpdate
}: BusinessAndKnowledgeSettingsProps) {
    const queryClient = useQueryClient();
    const businessProfileVideo = useVideoBySlug('business-profile-overview');
    const businessLocationVideo = useVideoBySlug('location-setup');
    const businessHoursVideo = useVideoBySlug('ai-books-appointments');
    const businessRulesVideo = useVideoBySlug('business-rules-every-call');
    const knowledgeServicesVideo = useVideoBySlug('knowledge-services-overview');
    const { data: heroCommands } = useQuery({
        queryKey: ['active-hero-commands'],
        queryFn: async (): Promise<UserHeroCommand[]> => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error("Not authenticated");

            const { data, error } = await supabase
                .from('user_hero_commands')
                .select(`is_active, id, hero_commands (title, description)`)
                .eq('is_active', true)
                .eq('user_id', session.user.id) as unknown as { data: UserHeroCommandRaw[]; error: Error | null }

            if (error) throw error;
            if (!data) return [];
            
            return data.map(item => ({
                id: item.id,
                title: item.hero_commands?.title || '',
                description: item.hero_commands?.description || '',
                is_active: item.is_active,
            })).sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase())) as UserHeroCommand[];
        },
    });




    const siteLinkPairs = allQaPairs?.filter(item => item.type === 'site_link') || [];
    const knowledgeBasePairs = allQaPairs?.filter(item => item.type === 'business_qa') || [];

    const [isQaItemDialogOpen, setIsQaItemDialogOpen] = useState(false);
    const [selectedQaItem, setSelectedQaItem] = useState<QAPair | null>(null);
    const [qaItemDialogType, setQaItemDialogType] = useState<QAPairType>('business_qa');

    const [isServiceItemDialogOpen, setIsServiceItemDialogOpen] = useState(false);
    const [selectedServiceItem, setSelectedServiceItem] = useState<BusinessService | null>(null);

    const [isDocumentUploadModalOpen, setIsDocumentUploadModalOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [deletingFileId, setDeletingFileId] = useState<string | null>(null);

    const qaForm = useForm<QAItemFormData>({
        resolver: zodResolver(qaItemFormSchema),
        defaultValues: { question: '', answer: '' }
    });

    const siteLinkForm = useForm<SiteLinkFormData>({
        resolver: zodResolver(siteLinkFormSchema),
        defaultValues: { question: '', answer: '' }
    });
    
    const serviceItemForm = useForm<ServiceItemFormData>({
        resolver: zodResolver(serviceItemFormSchema),
        defaultValues: { service_name: '', duration_minutes: 30, service_description: '' }
    });

    useEffect(() => {
        if (isQaItemDialogOpen) {
            if (qaItemDialogType === 'business_qa') {
                qaForm.reset({ question: selectedQaItem?.question ?? '', answer: selectedQaItem?.answer ?? '' }, { keepErrors: false, keepDirty: false });
            } else if (qaItemDialogType === 'site_link') {
                siteLinkForm.reset({ question: selectedQaItem?.question ?? '', answer: selectedQaItem?.answer ?? '' }, { keepErrors: false, keepDirty: false });
            }
        } else {
            setSelectedQaItem(null);
        }
    }, [selectedQaItem, isQaItemDialogOpen, qaItemDialogType, qaForm, siteLinkForm]);

    useEffect(() => {
        if (isServiceItemDialogOpen && selectedServiceItem) {
            serviceItemForm.reset({ service_name: selectedServiceItem.service_name, duration_minutes: selectedServiceItem.duration_minutes, service_description: selectedServiceItem.service_description ?? ''}, { keepErrors: false, keepDirty: false });
        } else if (!isServiceItemDialogOpen) {
            serviceItemForm.reset({ service_name: '', duration_minutes: 30, service_description: '' }, { keepErrors: false, keepDirty: false });
            setSelectedServiceItem(null);
        }
    }, [selectedServiceItem, isServiceItemDialogOpen, serviceItemForm]);

    // Consolidated Toast Messages
    const showAddedToast = (itemName: string = "Item") => {
        toast.success(`${itemName} added!`, {
            icon: <Sparkles className="h-5 w-5 text-green-500" />,
        });
    };
    const showUpdatedToast = (itemName: string = "Item") => {
        toast.info(`${itemName} updated!`, {
            icon: <CheckCircle2 className="h-5 w-5 text-blue-500" />,
        });
    };
    const showRemovedToast = (itemName: string = "Item") => {
        toast.message(`${itemName} removed.`, { 
            icon: <Trash2 className="h-5 w-5 text-gray-500" />,
        });
    };


    const createQaItem = useMutation({
        mutationFn: async (data: (QAItemFormData | SiteLinkFormData) & { type: QAPairType }) => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error("Not authenticated");
            const payload: { question: string; answer?: string; type: QAPairType } = { question: data.question, type: data.type };
            if (data.type === 'business_qa' && 'answer' in data && data.answer) payload.answer = data.answer;
            else if (data.type === 'site_link' && 'answer' in data && data.answer) payload.answer = data.answer; 

            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/business-qa`, { method: 'POST', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const resBody = await res.json().catch(() => ({ error: `HTTP ${res.status} failed to create item` }));
            if (!res.ok) throw new Error(resBody.error || `HTTP ${res.status}`);
            return resBody as QAPair;
        },
        onSuccess: (_createdData, variables) => { 
            queryClient.invalidateQueries({ queryKey: ['business-qa-items'] }); 
            setIsQaItemDialogOpen(false); 
            const typeName = variables.type === 'site_link' ? 'Page' : 'Answer'; 
            showAddedToast(typeName);
            if (variables.type === 'site_link') {
                 toast.info('AI is reading the page...', { duration: 3000 });
            } else if (variables.type === 'business_qa') {
                if (triggerVapiConfigUpdate) triggerVapiConfigUpdate(); 
            }
        },
        onError: (err: Error, variables) => { const typeName = variables.type === 'site_link' ? 'Page' : 'Answer'; toast.error(`Failed to add ${typeName}: ${err.message}`); },
    });

    const updateQaItem = useMutation({
        mutationFn: async (data: (QAItemFormData | SiteLinkFormData) & { id: string; type: QAPairType }) => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error("Not authenticated");
            const payload: { id: string; question: string; answer?: string } = { id: data.id, question: data.question };
            if ('answer' in data) payload.answer = data.answer ?? '';
            if (data.type !== 'site_link' && (!payload.answer || payload.answer.trim() === '')) {
                throw new Error("Answer cannot be empty for Q&A type.");
            }
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/business-qa`, { method: 'PUT', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const resBody = await res.json().catch(() => ({ error: `HTTP ${res.status} failed to update item` }));
            if (!res.ok) throw new Error(resBody.error || `HTTP ${res.status}`);
            return resBody as QAPair;
        },
        onSuccess: (updatedData) => { 
            queryClient.invalidateQueries({ queryKey: ['business-qa-items'] }); 
            setIsQaItemDialogOpen(false); 
            const typeName = updatedData?.type === 'site_link' ? 'Page' : 'Answer'; 
            showUpdatedToast(typeName);
            if (updatedData?.type === 'site_link') {
                toast.info('Page updated. AI will re-read if needed.', { duration: 3000 });
            } else if (updatedData?.type === 'business_qa') {
                if (triggerVapiConfigUpdate) triggerVapiConfigUpdate(); 
            }
        },
        onError: (err: Error, variables) => { const typeName = variables.type === 'site_link' ? 'Page' : 'Answer'; toast.error(`Failed to update ${typeName}: ${err.message}`); },
    });
    
    const [deletingQaItemId, setDeletingQaItemId] = useState<string | null>(null);
    const deleteQaItem = useMutation({
        mutationFn: async (id: string) => {
            setDeletingQaItemId(id);
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error("Not authenticated");
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/business-qa`, { method: 'DELETE', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) });
            if (res.status === 204) return { success: true };
            const resBody = await res.json().catch(() => ({ error: `Failed to parse delete response (Status: ${res.status})` }));
            if (!res.ok) throw new Error(resBody.error || `HTTP ${res.status} failed to delete item`);
            return resBody;
        },
        onMutate: async (idToDelete) => { await queryClient.cancelQueries({ queryKey: ['business-qa-items'] }); const prev = queryClient.getQueryData<QAPair[]>(['business-qa-items']); queryClient.setQueryData<QAPair[]>(['business-qa-items'], (old) => old ? old.filter(qa => qa.id !== idToDelete) : []); return { previousQA: prev }; },
        onError: (err: Error, idToDelete, context) => { if (context?.previousQA) { queryClient.setQueryData(['business-qa-items'], context.previousQA); } const deletedItem = (context?.previousQA as QAPair[] | undefined)?.find(qa => qa.id === idToDelete); const typeName = deletedItem?.type === 'site_link' ? 'Page' : 'Answer'; toast.error(`Failed to remove ${typeName}: ${err.message}`); },
        onSuccess: (_data, idToDelete, context) => { 
            const deletedItem = (context?.previousQA as QAPair[] | undefined)?.find(qa => qa.id === idToDelete); 
            const typeName = deletedItem?.type === 'site_link' ? 'Page' : 'Answer'; 
            showRemovedToast(typeName);
            if (deletedItem?.type === 'site_link') {
                 // No specific post-delete toast, covered by generic
            } else if (deletedItem?.type === 'business_qa') {
                if (triggerVapiConfigUpdate) triggerVapiConfigUpdate(); 
            }
        },
        onSettled: () => { queryClient.invalidateQueries({ queryKey: ['business-qa-items'] }); setDeletingQaItemId(null); },
    });

    const createServiceItem = useMutation({
        mutationFn: async (data: ServiceItemFormData) => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error("Not authenticated");
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/business-services`, { method: 'POST', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
            const resBody = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
            if (!res.ok) throw new Error(resBody.error || `HTTP ${res.status}`);
            return resBody as BusinessService;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['business-services-list'] });
            setIsServiceItemDialogOpen(false);
            if (triggerVapiConfigUpdate) triggerVapiConfigUpdate();
            showAddedToast("Booking type");
        },
        onError: (err: Error) => { toast.error(`Failed to add booking type: ${err.message}`); },
    });
    const updateServiceItem = useMutation({
        mutationFn: async (data: ServiceItemFormData & { id: string }) => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error("Not authenticated");
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/business-services`, { method: 'PUT', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
            const resBody = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
            if (!res.ok) throw new Error(resBody.error || `HTTP ${res.status}`);
            return resBody as BusinessService;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['business-services-list'] });
            setIsServiceItemDialogOpen(false);
            if (triggerVapiConfigUpdate) triggerVapiConfigUpdate();
            showUpdatedToast("Booking type");
        },
        onError: (err: Error) => { toast.error(`Failed to update booking type: ${err.message}`); },
    });
    const [deletingServiceItemId, setDeletingServiceItemId] = useState<string | null>(null);
    const deleteServiceItem = useMutation({
        mutationFn: async (id: string) => {
            setDeletingServiceItemId(id);
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error("Not authenticated");
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/business-services`, { method: 'DELETE', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) });
            if (res.status === 204 || (res.ok && res.headers.get('content-length') === '0')) return { success: true };
            const resBody = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
            if (!res.ok) throw new Error(resBody.error || `HTTP ${res.status}`);
            return resBody;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['business-services-list'] });
            if (triggerVapiConfigUpdate) triggerVapiConfigUpdate();
            showRemovedToast("Booking type");
        },
        onError: (err: Error) => { toast.error(`Failed to remove booking type: ${err.message}`); },
        onSettled: () => { setDeletingServiceItemId(null); }
    });


    const onQaItemDialogSubmit = (formData: QAItemFormData | SiteLinkFormData) => {
        if (!isQaItemDialogOpen) return;
        const mutationPayload = { ...formData, type: qaItemDialogType };
        if (selectedQaItem) { updateQaItem.mutate({ ...mutationPayload, id: selectedQaItem.id }); }
        else { createQaItem.mutate(mutationPayload); }
    };

    const onServiceItemSubmit = (formData: ServiceItemFormData) => {
        if (!isServiceItemDialogOpen) return;
        if (selectedServiceItem) { updateServiceItem.mutate({ ...formData, id: selectedServiceItem.id }); }
        else { createServiceItem.mutate(formData); }
    };

    const openQaItemDialog = (type: QAPairType, item: QAPair | null = null) => {
        setQaItemDialogType(type);
        setSelectedQaItem(item);
        setIsQaItemDialogOpen(true);
    };
    const openServiceItemDialog = (item: BusinessService | null = null) => {
        setSelectedServiceItem(item);
        setIsServiceItemDialogOpen(true);
    };
    
    const handleDeleteQaItem = (id: string) => { if (deletingQaItemId !== id) { deleteQaItem.mutate(id); }};
    const handleDeleteServiceItem = (id: string) => { if (deletingServiceItemId !== id) { deleteServiceItem.mutate(id); } };

    const truncateText = (text: string | null | undefined, maxLength: number): string => {
        if (!text) return ''; return text.length <= maxLength ? text : text.substring(0, maxLength) + '...';
    };

    const isQaItemProcessing = createQaItem.isPending || updateQaItem.isPending || deleteQaItem.isPending || !!deletingQaItemId;
    const isServiceItemProcessing = createServiceItem.isPending || updateServiceItem.isPending || deleteServiceItem.isPending || !!deletingServiceItemId;

    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadFile = useMutation({
        mutationFn: async (file: File) => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated');
            if (file.size > 5 * 1024 * 1024) { 
                throw new Error('File is too large. Maximum size is 5MB.');
            }
            const allowedTypes = ['application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(file.type)) {
                throw new Error('Invalid file type. Only PDF, TXT, DOC, DOCX are allowed.');
            }

            const form = new FormData();
            form.append('file', file);
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/knowledge-files`, { method: 'POST', headers: { Authorization: `Bearer ${session.access_token}` }, body: form });
            const json = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
            if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
            return json as KnowledgeFile;
        },
        onSuccess: () => { 
            queryClient.invalidateQueries({ queryKey: ['knowledge-files'] }); 
            showAddedToast("Document");
            if (fileInputRef.current) fileInputRef.current.value = ''; 
            setIsDocumentUploadModalOpen(false);
        },
        onError: (err: Error) => { toast.error(`Upload failed: ${err.message}`); }
    });

    const deleteFile = useMutation({
        mutationFn: async (id: string) => {
            setDeletingFileId(id);
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated');
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/knowledge-files`, { method: 'DELETE', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) });
            
            if (res.status === 204 || (res.ok && res.headers.get('content-length') === '0')) {
                return { success: true };
            }

            const json = await res.json().catch(() => ({ error: `HTTP ${res.status} - Failed to parse response` }));
            if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
            return json;
        },
        onSuccess: () => { 
            queryClient.invalidateQueries({ queryKey: ['knowledge-files'] }); 
            showRemovedToast("Document");
        },
        onError: (err: Error) => { toast.error(`Delete failed: ${err.message}`); },
        onSettled: () => {
            setDeletingFileId(null);
        }
    });

    const handleFileUploadFromModal = (file: File | null) => {
        if (file) {
            uploadFile.mutate(file);
        } else {
            toast.error("No file selected or file is invalid.");
        }
    };

    const handleFileDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            handleFileUploadFromModal(event.dataTransfer.files[0]);
            event.dataTransfer.clearData();
        }
    };
    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };
    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };
    const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            handleFileUploadFromModal(event.target.files[0]);
        }
    };

    const handleDeleteFile = (id: string) => { if (!deleteFile.isPending) deleteFile.mutate(id); };

    const isFileProcessing = uploadFile.isPending || deleteFile.isPending;
    const isAnyItemProcessing = isQaItemProcessing || isServiceItemProcessing || isFileProcessing;


    if (activeTab === "business") {
        return (
            <Card className="shadow-lg">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="flex items-center text-2xl">
                                <Briefcase className="mr-3 h-7 w-7 text-indigo-600" />
                                Business Profile
                            </CardTitle>
                            <CardDescription>Essential info your AI needs to take calls.</CardDescription>
                        </div>
                        <InfoModal 
                            title="Business Profile Overview" 
                            description={
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-700 mb-4">
                                        Set up your business profile to give your AI Employee the essential information it needs to represent your business effectively.
                                    </p>
                                    
                                    {businessProfileVideo && <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                        <iframe
                                            src={businessProfileVideo || "https://www.loom.com/embed/d3f68d2248834d698e967725018acd01?sid=4aa94f5e-4826-4023-b837-8695811e31e5"}
                                            frameBorder="0"
                                            allowFullScreen
                                            className="absolute top-0 left-0 w-full h-full rounded-lg"
                                        />
                                    </div>}
                                    {businessProfileVideo && <VideoLink to='business-profile-overview' />}

                                    <div className="space-y-3">
                                        <div>
                                            <h4 className="font-medium mb-1">Key Fields:</h4>
                                            <ul className="text-sm space-y-1 text-gray-600">
                                                <li><strong>AI's Direct Line:</strong> Your dedicated phone number for AI calls</li>
                                                <li><strong>Business Name:</strong> How your AI introduces your business</li>
                                                <li><strong>Location:</strong> Where you serve customers (can be remote)</li>
                                                <li><strong>Business Hours:</strong> When you're available for appointments</li>
                                                <li><strong>Hero Commands:</strong> Global business rules and scripts</li>
                                                <li><strong>Standard Appointment Length:</strong> Default duration for bookings</li>
                                            </ul>
                                        </div>
                                        
                                        <div>
                                            <h4 className="font-medium mb-1">How It Works:</h4>
                                            <ul className="text-sm space-y-1 text-gray-600">
                                                <li>AI uses this info in all customer conversations</li>
                                                <li>Determines when and how to book appointments</li>
                                                <li>Provides consistent business representation</li>
                                                <li>Enables personalized customer interactions</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            }
                            triggerClassName="ml-2"
                        />
                    </div>
                </CardHeader>
                <CardContent className="space-y-10 pt-6">
                     <div>
                        <div className="flex items-center mb-1">
                            <Label htmlFor="ai_phone_number_display" className="text-lg font-semibold block">Customers Call This Number</Label>
                            <InfoModal title="Your AI's Direct Line" description="Share this number with customers or forward your existing line here. This is the dedicated phone number for your AI Employee." triggerClassName="ml-1"/>
                        </div>
                        <p className="text-sm text-gray-500 mb-2">Your dedicated AI phone line.</p>
                        {isSettingsFetching && !settings?.vapi_phone_number && (<div className="flex items-center gap-2 p-3 bg-slate-100 border border-slate-200 rounded-md text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Getting your number...</div>)}
                        {!isSettingsFetching && settings?.vapi_phone_number ? ( 
    <motion.div 
        initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
        className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg shadow-sm">
        <span className="font-mono text-xl text-green-800 flex-grow tracking-wider">
            {(() => {
                const phone = settings.vapi_phone_number.replace(/\D/g, '');
                if (phone.length === 10) {
                    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
                } else if (phone.length === 11 && phone[0] === '1') {
                    return `+1 (${phone.slice(1, 4)})-${phone.slice(4, 7)}-${phone.slice(7)}`;
                } else if (phone.length === 11) {
                    return `+${phone[0]} (${phone.slice(1, 4)})-${phone.slice(4, 7)}-${phone.slice(7)}`;
                } else {
                    return settings.vapi_phone_number;
                }
            })()}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={() => copyToClipboard(settings.vapi_phone_number)} className="text-green-700 hover:text-green-800">
            <AnimatedIcon><Copy className="w-5 h-5 mr-1" /></AnimatedIcon> Copy
        </Button>
    </motion.div> 
) : !isSettingsFetching && (<div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">{isSetupComplete ? 'Phone number loading...' : 'Save to get your AI phone number.'}</div>)}
                    </div>

                    <div>
                        <div className="flex items-center mb-1"><Label htmlFor="business_name" className="text-lg font-semibold block">Business Name</Label><InfoModal title="Your Business Name" description="Enter the official name of your business. The AI will use this name when introducing itself and referring to the business during calls." triggerClassName="ml-1"/></div>
                        <p className="text-sm text-gray-500 mb-2">What customers will hear when your AI answers.</p>
                        <Input id="business_name" {...mainFormRegister('business_name')} placeholder="e.g., 'Radiant Smiles Dental' or 'John's Plumbing Services'" className="mt-1 block w-full" type="text" />
                        {mainFormErrors.business_name && <p className="text-red-600 text-sm mt-1">{mainFormErrors.business_name.message}</p>}
                    </div>
                    <div>
                        <div className="flex items-center mb-1">
                            <Label htmlFor="business_address" className="text-lg font-semibold block">Location</Label>
                            <InfoModal 
                                title="Location Setup" 
                                description={
                                    <div className="space-y-4">
                                        <p className="text-sm text-gray-700 mb-4">
                                            This field is much more than just a physical address. It's instructions for your AI about where and how you serve customers.
                                        </p>
                                            {businessLocationVideo && <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                            <iframe
                                                src={businessLocationVideo || "https://www.loom.com/embed/499d06c0cc714bdc971fed57c325c4f7?sid=a9c06eff-ab1b-4bef-9860-dd054f142f14"}
                                                frameBorder="0"
                                                allowFullScreen
                                                className="absolute top-0 left-0 w-full h-full rounded-lg"
                                            />
                                        </div>}
                                        {businessLocationVideo && <VideoLink to='location-setup' />}

                                        <div className="space-y-3">
                                            <div>
                                                <h4 className="font-medium mb-1">Advanced Strategies:</h4>
                                                <ul className="text-sm space-y-1 text-gray-600">
                                                    <li><strong>Remote Business:</strong> "We do not have a physical location. All appointments are done via Zoom."</li>
                                                    <li><strong>Multiple Locations:</strong> "If lead is in area A, meet at location A. If lead is in area B, meet at location B."</li>
                                                    <li><strong>Service-Specific:</strong> "For appointment A, we meet over Zoom. For appointment B, we meet on Google Meet."</li>
                                                </ul>
                                            </div>
                                            
                                            <div>
                                                <h4 className="font-medium mb-1">How AI Uses This:</h4>
                                                <ul className="text-sm space-y-1 text-gray-600">
                                                    <li>Discusses location with customers during calls</li>
                                                    <li>Determines meeting methods for appointments</li>
                                                    <li>Provides directions or meeting instructions</li>
                                                    <li>Adapts conversation based on service type</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                }
                                triggerClassName="ml-1"
                            />
                        </div>
                        <p className="text-sm text-gray-500 mb-2">Where you serve customers (used for directions and local search).</p>
                        <Textarea id="business_address" {...mainFormRegister('business_address')} placeholder="e.g., 123 Main St, Anytown, CA, 98765 or 'Serving the Greater Anytown Area'" rows={2} className="mt-1 block w-full"/>
                        {mainFormErrors.business_address && <p className="text-red-600 text-sm mt-1">{mainFormErrors.business_address.message}</p>}
                    </div>
                    <div>
                        <div className="flex items-center mb-1">
                            <Label htmlFor="business_hours_raw" className="text-lg font-semibold block">When AI Books Appointments</Label>
                            <InfoModal 
                                title="Business Hours Setup" 
                                description={
                                    <div className="space-y-4">
                                        <p className="text-sm text-gray-700 mb-4">
                                            This field is more than just hours - it's instructions for your AI about when and how to book appointments.
                                        </p>
                                        
                                        {businessHoursVideo && <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                            <iframe
                                                src={businessHoursVideo || "https://www.loom.com/embed/1b666789431a463593c6c797b45c6eff?sid=f70b26c3-bb53-43bb-a7ec-20994fcac50a"}
                                                frameBorder="0"
                                                allowFullScreen
                                                className="absolute top-0 left-0 w-full h-full rounded-lg"
                                            />
                                        </div>}
                                        {businessHoursVideo && <VideoLink to='ai-books-appointments' />}

                                        <div className="space-y-3">
                                            <div>
                                                <h4 className="font-medium mb-1">Advanced Examples:</h4>
                                                <ul className="text-sm space-y-1 text-gray-600">
                                                    <li><strong>Basic:</strong> "Monday to Thursday, 10 AM to 8 PM"</li>
                                                    <li><strong>With Logic:</strong> "Open 10 AM to 8 PM, but appointments only available before noon"</li>
                                                    <li><strong>Service-Specific:</strong> "Open for questions all day, but dental care appointments only before noon"</li>
                                                </ul>
                                            </div>
                                            
                                            <div>
                                                <h4 className="font-medium mb-1">How AI Uses This:</h4>
                                                <ul className="text-sm space-y-1 text-gray-600">
                                                    <li>Determines when to book appointments</li>
                                                    <li>Discusses availability with customers</li>
                                                    <li>Applies business logic to scheduling</li>
                                                    <li>Provides context during conversations</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                }
                                triggerClassName="ml-1"
                            />
                        </div>
                        <p className="text-sm text-gray-500 mb-2">Tell your AI when to schedule appointments (e.g., 'Weekdays 9-5').</p>
                        <Textarea id="business_hours_raw" {...mainFormRegister('business_hours_raw')} placeholder="e.g., Monday to Friday 9am to 5pm, Saturday 10am to 2pm. We are closed on Sundays." rows={3} className="mt-1 block w-full"/>
                        {mainFormErrors.business_hours_raw && <p className="text-red-600 text-sm mt-1">{mainFormErrors.business_hours_raw.message}</p>}
                    </div>

                    <div className="pt-6 border-t">
                        <div className="flex items-center mb-2">
                            <Label htmlFor="advanced_user_instructions_text" className="text-lg font-semibold block">Business-Wide Instructions</Label>
                            <InfoModal 
                                title="Global Business Rules & Commands" 
                                description={
                                    <div className="space-y-4">
                                        <p className="text-sm text-gray-700 mb-4">
                                            These are your business-wide rules that apply to ALL calls and ALL services. Think of these as your company's core policies and procedures.
                                        </p>
                                        
                                        {businessRulesVideo && <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                            <iframe
                                                src={businessRulesVideo || "https://www.loom.com/embed/b87819fc4ef540ac92626dbf8d3319fc?sid=820c27b7-244b-48a4-860f-f22bfde7c6d8"}
                                                frameBorder="0"
                                                allowFullScreen
                                                className="absolute top-0 left-0 w-full h-full rounded-lg"
                                            />
                                        </div>}
                                        {businessRulesVideo && <VideoLink to='business-rules-every-call' />}

                                        <div className="space-y-3">
                                            <div>
                                                <h4 className="font-medium mb-1">Popular Use Cases:</h4>
                                                <ul className="text-sm space-y-1 text-gray-600">
                                                    <li><strong>Scripts:</strong> Give your AI specific words and sentences to say</li>
                                                    <li><strong>Lead Qualification:</strong> Always ask qualifying questions before booking</li>
                                                    <li><strong>Brand Control:</strong> Control wording and brand representation</li>
                                                    <li><strong>Compliance:</strong> Avoid specific words or phrases</li>
                                                </ul>
                                            </div>
                                            
                                            <div>
                                                <h4 className="font-medium mb-1">Examples:</h4>
                                                <ul className="text-sm space-y-1 text-gray-600">
                                                    <li>"Always offer a 10% discount if customer mentions our newsletter"</li>
                                                    <li>"If anyone asks about emergency service, explain our $50 after-hours fee"</li>
                                                    <li>"When confirming any appointment, always remind them to bring their ID"</li>
                                                    <li>"Always qualify leads: Are you located in our service area? Do you have at least $5,000 to invest?"</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                }
                                triggerClassName="ml-1"
                            />
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            Tell the AI receptionist what to always do or say on any call. Use this for scripts, reminders, and business policies.
                        </p>
                        
                        {heroCommands && heroCommands.length > 0 && (
                            <div className="flex flex-col gap-2 mb-6 py-2">

                              <div className="flex gap-2 mb-2">
                                <Label htmlFor="advanced_user_instructions_text" className="text-base text-gray-500 font-semibold block">Active Commands</Label>
                                <p className="text-xs font-bold  p-1 px-3 bg-gray-100 rounded-full text-gray-500">{heroCommands.length} ACTIVE</p>
                              </div>
                              <div className="flex gap-4 flex-wrap">
                                {heroCommands.map((command) => (
                                    <div key={command.id} className="flex gap-2 p-3 px-4 bg-gray-100 rounded-full max-w-1/5" >
                                          <div className='flex size-5 items-center justify-center  rounded-full bg-green-300'><Check className="size-4 text-white" /> </div>
                                          <h4 className="tracking-tight truncate text-sm font-semibold text-gray-500 first-letter:uppercase lowercase inline">{command.title}</h4>
                                    </div>
                                ))}
                              </div>
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                                                        <Label htmlFor="advanced_user_instructions_text" className="text-base text-gray-500 font-semibold block">Instructions for All Calls</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={isSavingOrSyncing}
                              onClick={async () => {
                                try {
                                  const spinner = document.getElementById('adv-gen-spinner') as HTMLSpanElement | null;
                                  spinner?.classList.remove('hidden');
                                  const { data: { session } } = await supabase.auth.getSession();
                                  if (!session) { toast.error('Please sign in.'); return; }
                                  const currentText = (document.getElementById('advanced_user_instructions_text') as HTMLTextAreaElement | null)?.value || '';
                                  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-advanced-instructions`, {
                                    method: 'POST',
                                    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ use_case: 'general_reception', tone: 'friendly', existing_instructions: currentText })
                                  });
                                  const json = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
                                  if (!res.ok) { throw new Error(json.error || `HTTP ${res.status}`); }
                                  const content = (json.generated_text as string) || '';
                                  type Reco = { inbound_first_message_mode?: 'assistant-speaks-first' | 'assistant-speaks-first-with-model-generated-message' | 'assistant-waits-for-user'; outbound_first_message_mode?: 'assistant-speaks-first' | 'assistant-speaks-first-with-model-generated-message' | 'assistant-waits-for-user'; call_activation_settings?: SettingsFormData['call_activation_settings']; default_appointment_duration_minutes?: number | null } | undefined;
                                  const rec: Reco = json.recommended_settings as Reco;
                                  if (!content || content.trim().length === 0) { throw new Error('Empty AI response'); }
                                  // Apply generated text
                                  mainFormSetValue('advanced_user_instructions_text', content, { shouldDirty: true, shouldTouch: true });
                                  // Apply recommended settings if present
                                  if (rec?.inbound_first_message_mode) mainFormSetValue('inbound_first_message_mode', rec.inbound_first_message_mode, { shouldDirty: true });
                                  if (rec?.outbound_first_message_mode) mainFormSetValue('outbound_first_message_mode', rec.outbound_first_message_mode, { shouldDirty: true });
                                  if (rec?.default_appointment_duration_minutes !== undefined) mainFormSetValue('default_appointment_duration_minutes', rec.default_appointment_duration_minutes ?? null, { shouldDirty: true });
                                  if (rec?.call_activation_settings) mainFormSetValue('call_activation_settings', rec.call_activation_settings, { shouldDirty: true });
                                  toast.success('Instructions written! Review and save to activate.');
                                } catch (e: unknown) {
                                  const message = e instanceof Error ? e.message : 'Unknown error';
                                  toast.error(`Could not write instructions: ${message}`);
                                } finally {
                                  const spinner = document.getElementById('adv-gen-spinner') as HTMLSpanElement | null;
                                  spinner?.classList.add('hidden');
                                }
                              }}
                              title="AI instantly writes professional instructions for your business"
                            >
                              <span id="adv-gen-spinner" className="hidden mr-2 inline-flex items-center"><Loader2 className="h-4 w-4 animate-spin"/></span>
                              <Sparkles className="mr-2 h-4 w-4"/> Write This for Me
                            </Button>
                          </div>
                        </div>
                        {/* Removed recommendation container since we apply directly via form API */}
                        <Controller
                            name="advanced_user_instructions_text"
                            control={mainFormControl}
                            render={({ field }) => (
                              <Textarea 
                                id="advanced_user_instructions_text"
                                placeholder={`Example qualifying process:

1. Greet: "Thanks for calling ABC Services. I can help you schedule an appointment or answer questions."

2. Identify need: "Are you a new or returning customer?"
   - New: "Great! What service are you interested in?"
   - Returning: "Welcome back! Are you calling about your existing service or something new?"

3. Qualify urgency: "When would you like to come in?"
   - Today/Tomorrow: Check emergency slots, mention rush fee
   - This week: Book normal appointment
   - Just browsing: Offer to email service info

4. Collect info & book: "I have [time] available. I'll need your name and phone number to confirm."

5. Close: "You'll receive a text confirmation. Please bring a photo ID. Anything else I can help with?"`}
                                rows={8}  
                                className="mt-1 block w-full"
                                {...field}
                                value={field.value || ''} 
                              />
                            )}
                        />
                        {mainFormErrors.advanced_user_instructions_text && <p className="text-red-600 text-sm mt-1">{mainFormErrors.advanced_user_instructions_text.message}</p>}
                    </div>

                    <div className="pt-6 border-t">
                        <div className="flex items-center mb-1"><Label htmlFor="default_appointment_duration_minutes" className="text-lg font-semibold block">Default Appointment Length</Label><InfoModal title="Standard Appointment Length" description="Set a default duration (in minutes) for appointments. If someone books a time without picking a specific service, the system will automatically make the appointment this long. Also used if the AI cannot determine service duration." triggerClassName="ml-1"/></div>
                        <p className="text-sm text-gray-500 mb-2">How long are typical appointments? (AI uses this when not specified)</p>                    
                        <Controller name="default_appointment_duration_minutes" control={mainFormControl} render={({ field }) => (<Input id="default_appointment_duration_minutes" type="number" className="w-32" placeholder="e.g., 30" {...field} value={field.value ?? ''} onChange={e => {const val = parseInt(e.target.value,10); field.onChange(isNaN(val) ? null : val);}} min="5" step="5" />)} />
                        {mainFormErrors.default_appointment_duration_minutes && <p className="text-red-600 text-sm mt-1">{mainFormErrors.default_appointment_duration_minutes.message}</p>}
                    </div>
                    <div className="pt-8 border-t mt-8">
                        <Button type="submit" disabled={!isMainFormDirty || isSavingOrSyncing} className="w-full sm:w-auto text-base px-6 py-3"> 
                            {isSavingOrSyncing ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Saving...</>) : (isSetupComplete ? (<><Save className="mr-2 h-5 w-5" /> Save & Activate AI</>) : (<><Sparkles className="mr-2 h-5 w-5" /> Save & AI Goes Live</>) )} 
                        </Button>
                        {!isMainFormDirty && !isSavingOrSyncing && !isSettingsFetching && (<p className="text-sm text-gray-500 mt-2 inline-block ml-4">All changes saved.</p>)}
                        {isSettingsFetching && <Loader2 className="ml-4 h-5 w-5 animate-spin inline-block text-gray-400" />}
                         {isMainFormDirty && !isSavingOrSyncing && (
                            <motion.div initial={{ opacity: 0}} animate={{ opacity: 1}} className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700">
                                <Lightbulb className="inline h-4 w-4 mr-1 -mt-1" /> You have unsaved changes. Save to activate!
                            </motion.div>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (activeTab === "knowledge") {
        return (
            <Card className="shadow-lg">
                <CardHeader>
                                    <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center text-2xl">
                            <BookOpen className="mr-3 h-7 w-7 text-indigo-600" />
                            Knowledge & Services
                        </CardTitle>
                        <CardDescription>Train your AI Employee on what it needs to know and the services it can offer.</CardDescription>
                    </div>
                    <InfoModal 
                        title="Knowledge & Services Overview" 
                        description={
                            <div className="space-y-4">
                                <p className="text-sm text-gray-700 mb-4">
                                    This tab is where you give knowledge about your business to your AI. There are two main sections with different purposes.
                                </p>
                                
                                {knowledgeServicesVideo && <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                    <iframe
                                        src={knowledgeServicesVideo || "https://www.loom.com/embed/64c050647a954e74844112327d4fa171?sid=e5e0106f-182f-4a8b-88cf-c1b5b267811c"}
                                        frameBorder="0"
                                        allowFullScreen
                                        className="absolute top-0 left-0 w-full h-full rounded-lg"
                                    />
                                </div>}
                                {knowledgeServicesVideo && <VideoLink to='knowledge-services-overview' />}

                                <div className="space-y-4">
                                    <div>
                                        <h4 className="font-medium mb-2 text-blue-700">What Your AI Always Knows (Blue Section)</h4>
                                        <p className="text-sm text-gray-600 mb-2">Information your AI knows by heart and can recall immediately:</p>
                                        <ul className="text-sm space-y-1 text-gray-600 ml-4">
                                            <li>• <strong>Services You Offer:</strong> Types of appointments your AI can book</li>
                                            <li>• <strong>Common Questions:</strong> Quick answers your AI gives instantly</li>
                                            <li>• <strong>Result:</strong> Smooth, immediate conversations</li>
                                        </ul>
                                    </div>
                                    
                                    <div>
                                        <h4 className="font-medium mb-2 text-violet-700">Deep Knowledge (Purple Section)</h4>
                                        <p className="text-sm text-gray-600 mb-2">Information your AI can look up when needed:</p>
                                        <ul className="text-sm space-y-1 text-gray-600 ml-4">
                                            <li>• <strong>Documents & Guides:</strong> Upload PDFs, DOCs, or text files</li>
                                            <li>• <strong>Website Pages:</strong> URLs your AI can scrape and search</li>
                                            <li>• <strong>Result:</strong> AI pauses to research, then provides detailed answers</li>
                                        </ul>
                                    </div>
                                    
                                    <div className="bg-blue-50 p-3 rounded-lg">
                                        <h5 className="font-medium text-blue-800 mb-1">Key Difference</h5>
                                        <p className="text-sm text-blue-700">
                                            Think of it like a front desk person: Blue section = things they know by heart. Purple section = things they need to look up in documents.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        }
                        triggerClassName="ml-2"
                    />
                </div>
                </CardHeader>
                <CardContent className="space-y-10">
                    {!isSetupComplete && 
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertCircle className="h-5 w-5 text-yellow-500" />
                                </div>
                                <div className="ml-3">
                                                                                <p className="text-sm text-yellow-700">
                                                Complete your AI setup above to unlock this section.
                                            </p>
                                </div>
                            </div>
                        </div>
                    }

                    {/* Section 1: Core AI Training */}
                    <div className="p-6 rounded-lg bg-slate-50 shadow-md border border-slate-200">
                        <div className="flex items-center mb-1">
                            <Brain className="h-8 w-8 text-sky-600 mr-3" />
                            <h2 className="text-2xl font-semibold text-slate-800">Instant Answers</h2>
                            <span className="ml-3 bg-sky-100 text-sky-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">Always Active</span>
                        </div>
                        <p className="text-base text-slate-600 mb-6 ml-11">Things your AI answers without pausing to look up.</p>
                        
                        <section className="mb-8 p-5 bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                                <div className="flex items-center">
                                   <PackageCheck className="h-7 w-7 text-sky-500 mr-3 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-xl font-semibold text-slate-700 flex items-center">
                                            Appointments AI Can Book
                                            {isSetupComplete && businessServices && <span className="ml-2 text-sm bg-sky-100 text-sky-700 font-medium px-2 py-0.5 rounded-full">{businessServices.length}</span>}
                                        </h3>
                                        <p className="text-sm text-gray-500">What customers can schedule with your AI.</p>
                                    </div>
                                </div>
                                <Button type="button" onClick={() => openServiceItemDialog(null)} disabled={!isSetupComplete || isAnyItemProcessing} className="w-full sm:w-auto flex-shrink-0">
                                    <AnimatedIcon><Plus className="mr-2 h-4 w-4" /></AnimatedIcon> New Booking Type
                                </Button>
                            </div>
                            {servicesLoading && isSetupComplete && <div className="flex items-center justify-center p-6"><Loader2 className="h-6 w-6 animate-spin mr-2 text-sky-600" /> Loading Services...</div>}
                            {servicesError && isSetupComplete && !servicesLoading && <p className="text-red-600 p-4 border rounded bg-red-50">Error loading services: {servicesError.message}</p>}
                            <AnimatePresence>
                                {isSetupComplete && !servicesLoading && !servicesError && businessServices && businessServices.length > 0 ? (
                                    <div className="space-y-3">
                                        {businessServices.map((service) => (
                                            <motion.div 
                                                key={service.id} 
                                                layout 
                                                variants={listItemVariants}
                                                initial="hidden"
                                                animate="visible"
                                                exit="exit"
                                                className={cn("p-4 border border-slate-200 rounded-md flex flex-col sm:flex-row justify-between items-start bg-white gap-4 shadow-xs hover:border-sky-300", (deleteServiceItem.isPending && deletingServiceItemId === service.id) ? "opacity-50" : "")}
                                            >
                                                <div>
                                                    <p className="font-medium text-slate-700">{service.service_name}</p>
                                                    <p className="text-sm text-gray-500">{service.duration_minutes} minutes</p>
                                                    {service.service_description && <p className="text-sm text-gray-500 mt-1">{truncateText(service.service_description, 100)}</p>}
                                                </div>
                                                <div className="flex space-x-2 flex-shrink-0">
                                                    <Button type="button" variant="outline" size="icon" onClick={() => openServiceItemDialog(service)} disabled={isAnyItemProcessing} className="h-9 w-9">
                                                        <AnimatedIcon><Pencil size={16}/></AnimatedIcon><span className="sr-only">Edit Service</span>
                                                    </Button>
                                                    <Button type="button" variant="destructive" size="icon" onClick={() => handleDeleteServiceItem(service.id)} disabled={isAnyItemProcessing || (deleteServiceItem.isPending && deletingServiceItemId === service.id)} className="h-9 w-9">
                                                        <AnimatedIcon>{(deleteServiceItem.isPending && deletingServiceItemId === service.id) ? <Loader2 size={16} className="animate-spin"/> : <Trash2 size={16}/>}</AnimatedIcon><span className="sr-only">Delete Service</span>
                                                    </Button>
                                                </div>
                                            </motion.div>
                                        ))}
                                        {businessServices.length > 0 && (
                                            <motion.div initial={{opacity:0}} animate={{opacity:1}} className="mt-3 p-2 bg-sky-50 text-sm text-sky-700 rounded-md">
                                                AI can book: {businessServices.slice(0,3).map(s => s.service_name).join(', ')}{businessServices.length > 3 ? ' and more' : ''}
                                            </motion.div>
                                        )}
                                    </div>
                                ) : isSetupComplete && !servicesLoading && !servicesError && (
                                    <motion.div variants={listItemVariants} initial="hidden" animate="visible" className="text-center text-gray-500 py-10 border-2 border-dashed border-gray-200 rounded-lg bg-slate-100">
                                        <PackageCheck className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                                        <p className="font-semibold">No booking types yet.</p>
                                        <p className="text-sm">Add what customers can schedule.</p>
                                        <p className="text-xs text-slate-400 mt-3"><Lightbulb className="inline h-3 w-3 mr-1"/> Most businesses add 3-5 core services.</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                             {isSetupComplete && !servicesLoading && !servicesError && businessServices && businessServices.length > 0 && (
                                <div className="mt-4 text-sm text-slate-500">
                                    <ChevronRight className="inline h-4 w-4 mr-1 -mt-1" /> Next: Add quick answers about these services.
                                </div>
                            )}
                        </section>

                        <section className="p-5 bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                             <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                                <div className="flex items-center">
                                   <MessageSquareText className="h-7 w-7 text-sky-500 mr-3 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-xl font-semibold text-slate-700 flex items-center">
                                            Quick Answers AI Gives
                                            {isSetupComplete && knowledgeBasePairs && <span className="ml-2 text-sm bg-sky-100 text-sky-700 font-medium px-2 py-0.5 rounded-full">{knowledgeBasePairs.length}</span>}
                                        </h3>
                                        <p className="text-sm text-gray-500">Answers your AI gives without hesitation.</p>
                                    </div>
                                </div>
                                <Button type="button" onClick={() => openQaItemDialog('business_qa', null)} disabled={!isSetupComplete || isAnyItemProcessing} title={!isSetupComplete ? "Complete AI setup first" : "Add new Q&A"} className="w-full sm:w-auto flex-shrink-0">
                                     <AnimatedIcon><Plus className="mr-2 h-4 w-4" /></AnimatedIcon> Quick Answer
                                </Button>
                            </div>
                            {qaLoading && isSetupComplete && <div className="flex items-center justify-center p-6 text-slate-gray"><Loader2 className="h-6 w-6 animate-spin mr-2 text-sky-600" /> Loading Questions...</div>}
                            {qaError && isSetupComplete && !qaLoading && <p className="text-red-600 p-4 border border-red-200 bg-red-50 rounded">Error loading questions: {qaError.message}</p>}
                            <AnimatePresence>
                            {isSetupComplete && !qaLoading && !qaError && knowledgeBasePairs.length > 0 ? (
                                <div className="space-y-3">
                                {knowledgeBasePairs.map((item) => (
                                    <motion.div 
                                        key={item.id} 
                                        layout
                                        variants={listItemVariants}
                                        initial="hidden"
                                        animate="visible"
                                        exit="exit"
                                        className={cn("p-4 border border-slate-200 rounded-md flex flex-col sm:flex-row justify-between items-start bg-white gap-4 shadow-xs hover:border-sky-300 transition-opacity", deletingQaItemId === item.id ? "opacity-50" : "opacity-100")}
                                    >
                                        <div className="flex-grow break-words min-w-0 pr-2">
                                            <p className="font-medium text-slate-700">{item.question}</p>
                                            <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{item.answer}</p>
                                            <p className="mt-2 text-xs text-sky-600 italic">Jade will say: "{truncateText(item.answer, 50)}"</p>
                                        </div>
                                        <div className="flex space-x-2 flex-shrink-0 w-full sm:w-auto">
                                            <Button type="button" variant="outline" size="icon" disabled={isAnyItemProcessing || deletingQaItemId === item.id} onClick={() => openQaItemDialog('business_qa', item)} className="h-9 w-9" title={`Edit Question: ${truncateText(item.question, 20)}`}>
                                               <AnimatedIcon><Pencil className="h-4 w-4" /></AnimatedIcon><span className="sr-only">Edit</span>
                                            </Button>
                                            <Button type="button" variant="destructive" size="icon" onClick={() => handleDeleteQaItem(item.id)} disabled={isAnyItemProcessing || deletingQaItemId === item.id} className="h-9 w-9" title={`Delete Question: ${truncateText(item.question, 20)}`}>
                                                <AnimatedIcon>{(deletingQaItemId === item.id) ? (<Loader2 className="h-4 w-4 animate-spin" />) : (<Trash2 className="h-4 w-4" />)}</AnimatedIcon><span className="sr-only">Delete</span>
                                            </Button>
                                        </div>
                                    </motion.div>
                                ))}
                                </div>
                            ) : isSetupComplete && !qaLoading && !qaError && (
                                <motion.div variants={listItemVariants} initial="hidden" animate="visible" className="text-center text-gray-500 py-10 border-2 border-dashed border-gray-200 rounded-lg bg-slate-100">
                                        <MessageSquareText className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                                        <p className="font-semibold">No quick answers yet.</p>
                                        <p className="text-sm">Add instant responses your AI should give.</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </section>
                    </div>

                    {/* Section 2: Extended Knowledge Base */}
                    <div className="p-6 rounded-lg bg-slate-50 shadow-md border border-slate-200 mt-12">
                        <div className="flex items-center mb-1">
                            <Database className="h-8 w-8 text-violet-600 mr-3" />
                            <h2 className="text-2xl font-semibold text-slate-800">Research & Details</h2>
                             <span className="ml-3 bg-violet-100 text-violet-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">Searchable</span>
                        </div>
                        <p className="text-base text-slate-600 mb-6 ml-11">Info your AI searches when customers ask detailed questions.</p>                        

                        <section className="mb-8 p-5 bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                                <div className="flex items-center">
                                   <FileText className="h-7 w-7 text-violet-500 mr-3 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-xl font-semibold text-slate-700 flex items-center">
                                            Guides & Documents
                                            {isSetupComplete && knowledgeFiles && <span className="ml-2 text-sm bg-violet-100 text-violet-700 font-medium px-2 py-0.5 rounded-full">{knowledgeFiles.length}</span>}
                                        </h3>
                                        <p className="text-sm text-gray-500">Upload guides, PDFs, or documents your AI can search.</p>
                                    </div>
                                </div>
                                <Button type="button" onClick={() => setIsDocumentUploadModalOpen(true)} disabled={!isSetupComplete || isAnyItemProcessing} className="w-full sm:w-auto flex-shrink-0">
                                    <AnimatedIcon><Plus className="mr-2 h-4 w-4" /></AnimatedIcon> Upload Document
                                </Button>
                            </div>
                            {filesLoading && isSetupComplete && <div className="flex items-center justify-center p-6 text-slate-gray"><Loader2 className="h-6 w-6 animate-spin mr-2 text-violet-600" /> Loading Materials...</div>}
                            {filesError && isSetupComplete && !filesLoading && <p className="text-red-600 p-4 border border-red-200 bg-red-50 rounded">Error loading materials: {filesError.message}</p>}
                            <AnimatePresence>
                            {isSetupComplete && !filesLoading && !filesError && knowledgeFiles && knowledgeFiles.length > 0 ? (
                                <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4">
                                    {knowledgeFiles.map(file => (
                                        <motion.div 
                                            key={file.id}
                                            layout
                                            variants={listItemVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                            className={cn("p-4 border border-slate-200 rounded-md bg-white flex flex-col justify-between shadow-xs hover:border-violet-300", (deleteFile.isPending && deletingFileId === file.id) ? "opacity-50" : "")}
                                        >
                                            <div>
                                                <div className="flex items-center mb-2">
                                                    <FileText size={20} className="mr-2 text-slate-500 flex-shrink-0"/>
                                                    <p className="font-medium text-slate-700 truncate" title={file.file_name}>{file.file_name}</p>
                                                </div>
                                                <p className="text-xs text-gray-500 mb-1">Added: {file.created_at ? new Date(file.created_at).toLocaleDateString() : 'N/A'}</p>
                                                {file.preview_text && <p className="text-sm text-gray-600 mt-1 line-clamp-2 italic">"{truncateText(file.preview_text, 100)}"</p>}
                                            </div>
                                            <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-200">
                                                <div className="flex items-center">
                                                    <CheckCircle2 className="inline-block w-4 h-4 text-green-600 mr-1.5" />
                                                    <span className="text-xs text-green-700 font-medium">Searchable by AI</span>
                                                </div>
                                                <Button 
                                                    type="button" 
                                                    variant="destructive" 
                                                    size="icon" 
                                                    onClick={() => handleDeleteFile(file.id)} 
                                                    disabled={isAnyItemProcessing || (deleteFile.isPending && deletingFileId === file.id)} 
                                                    className="h-9 w-9"
                                                    title={`Delete Material: ${file.file_name}`}
                                                >
                                                   <AnimatedIcon>{(deleteFile.isPending && deletingFileId === file.id) ? <Loader2 size={16} className="animate-spin"/> : <Trash2 size={16}/>}</AnimatedIcon>
                                                    <span className="sr-only">Delete Material</span>
                                                </Button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            ) : isSetupComplete && !filesLoading && !filesError && (
                                 <motion.div variants={listItemVariants} initial="hidden" animate="visible" className="text-center text-gray-500 py-10 border-2 border-dashed border-gray-200 rounded-lg bg-slate-100">
                                        <FileText className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                                        <p className="font-semibold">No documents uploaded yet.</p>
                                        <p className="text-sm">Upload documents for your AI to search.</p>
                                    </motion.div>
                                )}
                             </AnimatePresence>
                              {isSetupComplete && !filesLoading && !filesError && knowledgeFiles && knowledgeFiles.length > 0 && (
                                <div className="mt-4 text-sm text-slate-500">
                                    <ChevronRight className="inline h-4 w-4 mr-1 -mt-1" /> Next: Add website pages for detailed info.
                                </div>
                            )}
                        </section>

                        <section className="p-5 bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                                <div className="flex items-center">
                                   <ExternalLink className="h-7 w-7 text-violet-500 mr-3 flex-shrink-0" />
                                    <div>
                                        <h3 className="text-xl font-semibold text-slate-700 flex items-center">
                                            Website Pages to Search
                                            {isSetupComplete && siteLinkPairs && <span className="ml-2 text-sm bg-violet-100 text-violet-700 font-medium px-2 py-0.5 rounded-full">{siteLinkPairs.length}</span>}
                                        </h3>
                                        <p className="text-sm text-gray-500">Pages your AI reads to answer detailed questions.</p>
                                    </div>
                                </div>
                                <Button type="button" onClick={() => openQaItemDialog('site_link', null)} disabled={!isSetupComplete || isAnyItemProcessing} className="w-full sm:w-auto flex-shrink-0">
                                    <AnimatedIcon><LinkIcon className="mr-2 h-4 w-4" /></AnimatedIcon> Add Page
                                </Button>
                            </div>
                            {qaLoading && isSetupComplete && <div className="flex items-center justify-center p-6 text-slate-gray"><Loader2 className="h-6 w-6 animate-spin mr-2 text-violet-600" /> Loading Links...</div>}
                            {qaError && isSetupComplete && !qaLoading && <p className="text-red-600 p-4 border border-red-200 bg-red-50 rounded">Error loading links: {qaError.message}</p>}
                            <AnimatePresence>
                            {isSetupComplete && !qaLoading && !qaError && siteLinkPairs.length > 0 ? (
                                 <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4">
                                {siteLinkPairs.map((item) => {
                                    const isDeletingThis = deletingQaItemId === item.id;
                                    const isReadingContent = item.answer === '' && !qaLoading; 
                                    return (
                                    <motion.div 
                                        key={item.id} 
                                        layout
                                        variants={listItemVariants}
                                        initial="hidden"
                                        animate="visible"
                                        exit="exit"
                                        className={cn("p-4 border border-slate-200 rounded-md flex flex-col justify-between bg-white gap-2 shadow-xs hover:border-violet-300 transition-opacity", isDeletingThis ? "opacity-50" : "opacity-100")}
                                    >
                                        <div>
                                            <a href={item.question} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:text-blue-700 hover:underline text-sm break-all inline-flex items-center mb-1">
                                                <LinkIcon size={14} className="mr-1.5 flex-shrink-0"/> {truncateText(item.question, 60)}
                                            </a> 
                                            {isReadingContent ? (
                                                <p className="mt-1 text-sm text-slate-500 italic flex items-center"><Loader2 size={14} className="animate-spin mr-1.5"/> AI is saving this page content...</p>
                                            ) : (
                                                <p className="mt-1 text-sm text-gray-600 line-clamp-2 italic">Content Summary: "{item.answer ? truncateText(item.answer, 80) : '(No summary available or reading failed)'}"</p>
                                            )}
                                        </div>
                                        <div className="flex space-x-2 flex-shrink-0 w-full sm:w-auto mt-2 pt-2 border-t border-slate-200 justify-between">
                                        <div className="flex items-center">
                                            <CheckCircle2 className="inline-block w-4 h-4 text-green-600 mr-1.5" />
                                            <span className="text-xs text-green-700 font-medium">Searchable by AI</span>
                                        </div>
                                        <div className="flex space-x-2">
                                            <Button type="button" variant="outline" size="icon" disabled={isAnyItemProcessing || isDeletingThis} onClick={() => openQaItemDialog('site_link', item)} className="h-9 w-9" title={`Edit Site Link: ${item.question}`}>
                                                <AnimatedIcon><Pencil className="h-4 w-4" /></AnimatedIcon><span className="sr-only">Edit</span>
                                            </Button>
                                            <Button type="button" variant="destructive" size="icon" onClick={() => handleDeleteQaItem(item.id)} disabled={isAnyItemProcessing || isDeletingThis} className="h-9 w-9" title={`Delete Site Link: ${item.question}`}>
                                                <AnimatedIcon>{isDeletingThis ? (<Loader2 className="h-4 w-4 animate-spin" />) : (<Trash2 className="h-4 w-4" />)}</AnimatedIcon><span className="sr-only">Delete</span>
                                            </Button>
                                        </div>
                                    </div>
                                    </motion.div>
                                )})}
                                </div>
                            ) : isSetupComplete && !qaLoading && !qaError && (
                                <motion.div variants={listItemVariants} initial="hidden" animate="visible" className="text-center text-gray-500 py-10 border-2 border-dashed border-gray-200 rounded-lg bg-slate-100">
                                        <ExternalLink className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                                        <p className="font-semibold">No pages linked yet.</p>
                                        <p className="text-sm">Add website pages for your AI to search.</p>
                                    </motion.div>
                                )}
                                </AnimatePresence>
                        </section>
                    </div>
                
                    {/* Modals */}
                    <AlertDialog open={isQaItemDialogOpen} onOpenChange={setIsQaItemDialogOpen}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>
                                    {selectedQaItem 
                                        ? `Edit ${qaItemDialogType === 'site_link' ? 'Page' : 'Answer'}` 
                                        : `Add ${qaItemDialogType === 'site_link' ? 'Page' : 'Quick Answer'}`}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                    {qaItemDialogType === 'site_link' 
                                        ? (selectedQaItem ? "Edit the page URL or its content." : "Enter a webpage URL for your AI to search.")
                                        : "What question and answer should your AI know by heart?"}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <form onSubmit={qaItemDialogType === 'business_qa' ? qaForm.handleSubmit(onQaItemDialogSubmit as (data: QAItemFormData) => void) : siteLinkForm.handleSubmit(onQaItemDialogSubmit as (data: SiteLinkFormData) => void)} className="space-y-4 pt-4">
                                {qaItemDialogType === 'business_qa' && (
                                    <>
                                        <div>
                                            <Label htmlFor="qa_question_field">Customer Question</Label>
                                            <Input id="qa_question_field" {...qaForm.register('question')} className="mt-1 w-full" placeholder="What do customers ask? (e.g., 'What are your hours?')" />
                                            {qaForm.formState.errors.question && <p className="text-red-600 text-sm mt-1">{qaForm.formState.errors.question.message}</p>}
                                        </div>
                                        <div>
                                            <Label htmlFor="qa_answer_field">AI's Answer</Label>
                                            <Textarea id="qa_answer_field" {...qaForm.register('answer')} className="mt-1 w-full" rows={3} placeholder="Exact words AI will say (e.g., 'We're open Monday to Friday, 9 AM to 5 PM')" />
                                            {qaForm.formState.errors.answer && <p className="text-red-600 text-sm mt-1">{qaForm.formState.errors.answer.message}</p>}
                                        </div>
                                    </>
                                )}
                                {qaItemDialogType === 'site_link' && (
                                    <>
                                        <div>
                                            <Label htmlFor="sitelink_url_field">Page URL</Label>
                                            <Input id="sitelink_url_field" {...siteLinkForm.register('question')} className="mt-1 w-full" placeholder="https://yoursite.com/about" type="url" />
                                            {siteLinkForm.formState.errors.question && <p className="text-red-600 text-sm mt-1">{siteLinkForm.formState.errors.question.message}</p>}
                                        </div>
                                        {(selectedQaItem || siteLinkForm.getValues('answer')) && ( 
                                            <div>
                                                <Label htmlFor="sitelink_content_field">Page Content (Editable)</Label>
                                                <Textarea id="sitelink_content_field" {...siteLinkForm.register('answer')} className="mt-1 w-full" rows={6} placeholder={selectedQaItem ? "What's on this page..." : "(AI will read and summarize the page)"} />
                                                {siteLinkForm.formState.errors.answer && <p className="text-red-600 text-sm mt-1">{siteLinkForm.formState.errors.answer.message}</p>}
                                                {selectedQaItem && (<p className="text-xs text-gray-500 mt-1">You can edit how your AI understands this page.</p>)}
                                            </div>
                                        )}
                                    </>
                                )}
                                <AlertDialogFooter>
                                    <AlertDialogCancel type="button" onClick={() => setIsQaItemDialogOpen(false)}>Cancel</AlertDialogCancel>
                                    <Button type="submit" disabled={isQaItemProcessing}>
                                        {(createQaItem.isPending || updateQaItem.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        <Save size={16} className="mr-2"/>
                                        {selectedQaItem ? 'Save Changes' : 'Add'}
                                    </Button>
                                </AlertDialogFooter>
                            </form>
                        </AlertDialogContent>
                    </AlertDialog>

                    <AlertDialog open={isServiceItemDialogOpen} onOpenChange={setIsServiceItemDialogOpen}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>{selectedServiceItem ? 'Edit Booking Type' : 'Add New Booking Type'}</AlertDialogTitle>
                                <AlertDialogDescription>What can customers book? Add any special instructions for this specific appointment type.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <form onSubmit={serviceItemForm.handleSubmit(onServiceItemSubmit)} className="space-y-4 pt-4">
                                <div>
                                    <Label htmlFor="serviceNameModal">What Can They Book?</Label>
                                    <Input id="serviceNameModal" {...serviceItemForm.register('service_name')} className="mt-1 w-full" placeholder="e.g., Consultation, Cleaning, Repair"/>
                                    {serviceItemForm.formState.errors.service_name && <p className="text-red-600 text-sm mt-1">{serviceItemForm.formState.errors.service_name.message}</p>}
                                </div>
                                <div>
                                    <Label htmlFor="serviceDurationModal">How Long? (minutes)</Label>
                                    <Input id="serviceDurationModal" type="number" {...serviceItemForm.register('duration_minutes')} className="mt-1 w-full" placeholder="30" min="5" step="5"/>
                                    {serviceItemForm.formState.errors.duration_minutes && <p className="text-red-600 text-sm mt-1">{serviceItemForm.formState.errors.duration_minutes.message}</p>}
                                </div>
                                <div>
                                    <Label htmlFor="serviceDescriptionModal">Special Instructions for This Booking (Optional)</Label>
                                    <Textarea id="serviceDescriptionModal" {...serviceItemForm.register('service_description')} className="mt-1 w-full" placeholder="Any special rules? (e.g., 'Ask about insurance' or 'Requires $50 deposit' or '24hr cancellation policy')" rows={3}/>
                                    {serviceItemForm.formState.errors.service_description && <p className="text-red-600 text-sm mt-1">{serviceItemForm.formState.errors.service_description.message}</p>}
                                    <p className="text-xs text-gray-500 mt-1">
                                        <strong>💡 Tip:</strong> These rules only apply to this booking type. For rules that apply to all calls, use the Business Rules section.
                                    </p>
                                </div>
                                <AlertDialogFooter>
                                    <AlertDialogCancel type="button" onClick={() => setIsServiceItemDialogOpen(false)}>Cancel</AlertDialogCancel>
                                    <Button type="submit" disabled={isServiceItemProcessing}>
                                        {(createServiceItem.isPending || updateServiceItem.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                                        <Save size={16} className="mr-2"/>
                                        {selectedServiceItem ? 'Update' : 'Add'}
                                    </Button>
                                </AlertDialogFooter>
                            </form>
                        </AlertDialogContent>
                    </AlertDialog>

                    <AlertDialog open={isDocumentUploadModalOpen} onOpenChange={setIsDocumentUploadModalOpen}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Upload Guide or Document</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Upload a PDF, document, or guide your AI can search (max 5MB).
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <div className="py-4">
                                <div
                                    className={cn(
                                        "flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer",
                                        isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400",
                                        uploadFile.isPending ? "bg-gray-100" : "bg-gray-50"
                                    )}
                                    onDrop={handleFileDrop}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onClick={() => !uploadFile.isPending && fileInputRef.current?.click()}
                                >
                                    {uploadFile.isPending ? (
                                        <>
                                            <Loader2 className="h-10 w-10 text-blue-500 animate-spin mb-3" />
                                            <p className="text-sm text-gray-500">AI is learning from document...</p>
                                        </>
                                    ) : (
                                        <>
                                            <UploadCloud className="h-10 w-10 text-gray-400 mb-3" />
                                            <p className="text-sm text-gray-500">
                                                <span className="font-semibold text-blue-600">Click to upload</span> or drag and drop
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">PDF, TXT, DOC, DOCX (max 5MB)</p>
                                        </>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileInputChange}
                                    className="hidden"
                                    accept=".pdf,.txt,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    disabled={uploadFile.isPending}
                                />
                            </div>
                            <AlertDialogFooter>
                                <AlertDialogCancel type="button" onClick={() => setIsDocumentUploadModalOpen(false)} disabled={uploadFile.isPending}>
                                    Cancel
                                </AlertDialogCancel>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>

                </CardContent>
            </Card>
        );
    }
    return null; 
}